/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import type { VNode, ComponentChildren } from "preact";
import { useTranslation } from "../context/translation.js";

export interface BannerProps {
  type?: "warning" | "error" | "info";
  children: ComponentChildren;
  onDismiss?: () => void;
  actionText?: string;
  onAction?: () => void;
}

export function Banner({
  type = "warning",
  children,
  onDismiss,
  actionText,
  onAction,
}: BannerProps): VNode {
  const { t } = useTranslation();
  const bgColors = {
    warning: "bg-amber-50/90 border-amber-300 text-amber-950",
    error: "bg-red-50/90 border-red-300 text-red-950",
    info: "bg-blue-50/90 border-blue-300 text-blue-950",
  };

  const buttonStyles = {
    warning: "bg-amber-800 hover:bg-amber-900 text-white shadow-2xs",
    error: "bg-red-800 hover:bg-red-900 text-white shadow-2xs",
    info: "bg-blue-800 hover:bg-blue-900 text-white shadow-2xs",
  };

  return (
    <div
      {...(type === "error" ? { "data-error-banner": "" } : {})}
      role="alert"
      class={`mb-6 p-4 border rounded-xl shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${bgColors[type]}`}
    >
      <div class="flex items-start sm:items-center space-x-3 min-w-0 flex-1">
        <span
          class="font-semibold text-lg leading-none shrink-0"
          aria-hidden="true"
        >
          {type === "warning" ? "⚠️" : type === "error" ? "🛑" : "ℹ️"}
        </span>
        <div class="text-xs sm:text-sm font-medium leading-relaxed break-words">
          {children}
        </div>
      </div>
      <div class="flex items-center space-x-3 shrink-0 self-end sm:self-center ml-auto">
        {actionText && onAction && (
          <button
            type="button"
            onClick={onAction}
            class={`text-xs font-bold px-3.5 py-1.5 rounded-lg transition-colors whitespace-nowrap shrink-0 ${buttonStyles[type]}`}
          >
            {actionText}
          </button>
        )}
        {onDismiss && (
          <button
            type="button"
            onClick={onDismiss}
            class="text-sm font-bold opacity-60 hover:opacity-100 p-1.5 rounded-md hover:bg-black/10 transition-colors shrink-0"
            aria-label={t`Dismiss banner`}
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
}
